/**
 * CO and CE: one question per screen, « Valider », no going back. CO follows the flow of the
 * official test (D-106): the number, the instruction, a reading time, ONE listening (the
 * generated audio without controls, or the browser's voices, one per character), then an answer
 * time; when it runs out the answer given (or none) is sent and the next question comes. The
 * options open once the document has been heard. Training mode: the correction is shown before
 * « Suivant », without the answer clock, and the document has a full player (pause, back, listen
 * again at will, D-144) instead of the single listening.
 */

import {
  ApiError,
  academyAttemptsAnswerCreate,
  academyAttemptsAudioRetrieve,
  academyAttemptsNextCreate,
} from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { Check, CheckCircle2, Headphones, Play, XCircle } from "lucide-react";
import { hasPictures, questionProgram } from "@agencyos/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { readStorage, writeStorage } from "../lib/storage";
import { OnceAudio, speechAvailable, stopSpeaking, unlockOnFirstGesture } from "./audio";
import { ExamAbandon, ExamAudioPanel, ExamBar, ExamCountdown, ExamNav, ExamPill, focusQuestion, sequenceCells, useAnswerKeys } from "./ExamFrame";
import { ExamPictureView, PictureOptions } from "./ExamPicture";
import { speakLines, stopLines } from "./listening";
import { TrainingListen, TrainingNote } from "./ListeningReplay";
import "./listening.css";
import { type PlayerState, type StateQuestion, isObject, num, str } from "./types";

type Listen = "reading" | "idle" | "loading" | "playing" | "done";

/** Seconds left of a countdown that runs while `active` (null when not running). */
export function useCountdown(seconds: number, active: boolean, onEnd: () => void): number | null {
  const [left, setLeft] = useState<number | null>(null);
  const end = useRef(onEnd);
  useEffect(() => {
    end.current = onEnd;
  }, [onEnd]);
  useEffect(() => {
    if (!active || seconds <= 0) {
      setLeft(null);
      return;
    }
    const deadline = Date.now() + seconds * 1000;
    setLeft(seconds);
    const timer = window.setInterval(() => {
      const rest = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setLeft(rest);
      if (rest === 0) {
        window.clearInterval(timer);
        end.current();
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [seconds, active]);
  return left;
}

interface Correction {
  correct: boolean;
  answer: number;
  explanation: string;
}

const WAVE = [0, 1, 2, 3, 4, 5, 6, 7, 8];

/** « Cette épreuve est terminée » between two sections. */
export function SectionDone({ onContinue, busy }: { onContinue: () => void; busy: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="ac-surface ac-card ac-card--center ac-done">
      <span className="ac-done__icon" aria-hidden="true">
        <CheckCircle2 size={28} />
      </span>
      <p className="ac-title-3">{t("academy.player.sectionDone")}</p>
      <Button size="lg" onClick={onContinue} disabled={busy}>
        {t("academy.player.continue")}
      </Button>
    </div>
  );
}

const heardKey = (attemptId: string, questionId: string) => `academy.heard.${attemptId}.${questionId}`;

function Question({
  attemptId,
  question,
  skill,
  answered,
  total,
  kind,
  training,
  onChanged,
}: {
  attemptId: string;
  question: StateQuestion;
  skill: "co" | "ce";
  answered: number;
  total: number;
  kind: string;
  training: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<number | null>(null);
  const flow = skill === "co" ? question.flow : null;
  const [listen, setListen] = useState<Listen>(() =>
    skill === "co" && readStorage(heardKey(attemptId, question.id)) !== "1"
      ? flow && flow.readingSeconds > 0
        ? "reading"
        : "idle"
      : "done",
  );
  const [audioProblem, setAudioProblem] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correction, setCorrection] = useState<Correction | null>(null);
  const audio = useRef<OnceAudio | null>(null);
  const stem = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    focusQuestion(stem.current);
    unlockOnFirstGesture(); // iOS: the documents may then start by themselves
    return () => {
      audio.current?.stop();
      stopSpeaking();
      stopLines();
    };
  }, []);

  const heard = () => {
    writeStorage(heardKey(attemptId, question.id), "1");
    setListen("done");
  };

  const play = async () => {
    if (question.has_audio) {
      if (audio.current) {
        // Autoplay was refused: the learner's click starts the same (single) listening, with
        // the link already given (a second request would be refused, D-114).
        setListen("playing");
        const outcome = await audio.current.resume();
        if (outcome === "blocked") setListen("idle");
        return;
      }
      // The link is given once (the server keeps it): a reload does not give a second listening.
      writeStorage(heardKey(attemptId, question.id), "1");
      setListen("loading");
      try {
        const response = await academyAttemptsAudioRetrieve(attemptId, question.id);
        const player = new OnceAudio({
          onEnded: heard,
          onFailed: () => {
            setAudioProblem(true);
            heard();
          },
        });
        audio.current = player;
        setListen("playing");
        const outcome = await player.start(response.data.url);
        if (outcome === "blocked") setListen("idle"); // the button starts it
      } catch {
        setAudioProblem(true);
        heard();
      }
      return;
    }
    if (!speechAvailable() || !question.script_for_speech.length) {
      writeStorage(heardKey(attemptId, question.id), "1");
      setAudioProblem(true);
      heard();
      return;
    }
    setListen("playing");
    const lines = questionProgram(question.script_for_speech, flow, question.stem);
    const result = await speakLines(lines, { lang: "fr", preferred: "fr-FR" });
    if (result === "blocked") {
      setListen("idle"); // nothing was heard: the button starts it
      return;
    }
    writeStorage(heardKey(attemptId, question.id), "1");
    if (result === "done") heard();
  };

  const playRef = useRef(play);
  playRef.current = play;
  // training: no automatic single listening, the learner starts the player (D-144)
  const startListening = useCallback(() => (training ? setListen("idle") : void playRef.current()), [training]);
  const loadAudio = useCallback(
    async () => (await academyAttemptsAudioRetrieve(attemptId, question.id)).data.url,
    [attemptId, question.id],
  );
  const trainingLines = useMemo(
    () => (skill === "co" ? questionProgram(question.script_for_speech, flow, question.stem) : []),
    [skill, question.script_for_speech, flow, question.stem],
  );
  const trainingPlayer = skill === "co" && training && listen !== "reading";
  const readingLeft = useCountdown(flow?.readingSeconds ?? 0, listen === "reading", startListening);

  const validate = async (given: number | null) => {
    if (given === null) return;
    setSending(true);
    setError(null);
    try {
      const response = await academyAttemptsAnswerCreate(attemptId, { question: question.id, choice: given });
      const data: unknown = response.data;
      if (training && isObject(data) && typeof data.correct === "boolean") {
        setCorrection({ correct: data.correct, answer: num(data.answer, -1), explanation: str(data.explanation) });
        setSending(false);
        return;
      }
      await onChanged();
    } catch (failure) {
      // Time up, or already answered (a second tab): the server state decides what comes next.
      if (failure instanceof ApiError && failure.status === 400) await onChanged();
      else setError(t("academy.player.networkError"));
      setSending(false);
    }
  };

  const optionsOpen = skill === "ce" || listen === "done";
  const letters = ["A", "B", "C", "D"];
  // Exam: when the answer time runs out, the answer given (or none, -1) is sent, as on the day.
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const choiceRef = useRef(choice);
  choiceRef.current = choice;
  const timeUp = useCallback(() => void validateRef.current(choiceRef.current ?? -1), []);
  const answerLeft = useCountdown(
    flow?.answerSeconds ?? 0,
    skill === "co" && !training && listen === "done" && correction === null && !sending,
    timeUp,
  );

  const canChoose = optionsOpen && !sending && correction === null;
  useAnswerKeys(
    question.options.length,
    (index) => {
      if (canChoose) setChoice(index);
    },
    () => {
      if (correction) void onChanged();
      else if (canChoose) void validate(choice);
    },
  );

  // D-173: the frame shows the question's level (never the placement's in an exam: its next level
  // would tell whether the last answer was right).
  const showLevel = Boolean(question.level) && (training || kind !== "placement");
  const position = Math.min(answered + 1, total || answered + 1);

  return (
    <section className="ac-question ac-xq" aria-labelledby={`q-${question.id}`}>
      <ExamNav cells={sequenceCells(total, [], position)} />
      <div className="ac-question__sheet">
        {skill === "co" && flow?.consigne && (
          <div className="ac-flow">
            <p className="ac-flow__consigne" lang="fr">
              {flow.consigne}
            </p>
          </div>
        )}
        {trainingPlayer && (
          <ExamAudioPanel note={<TrainingNote />}>
            <div lang="fr">
              <TrainingListen
                load={question.has_audio ? loadAudio : null}
                lines={trainingLines}
                onStart={() => {
                  if (listen !== "done") heard();
                }}
              />
            </div>
          </ExamAudioPanel>
        )}
        {skill === "co" && !trainingPlayer && (
          <ExamAudioPanel
            playing={listen === "playing" && question.has_audio}
            note={
              !training && (
                <>
                  <p className="ac-footnote">{t("academy.player.listenOnce")}</p>
                  <p className="ac-footnote">{t("listening.training.tip")}</p>
                </>
              )
            }
          >
            <div className={`ac-listen ac-listen--${listen === "reading" ? "idle" : listen}`} lang="fr">
              {listen === "reading" && <p className="ac-footnote">{t("listening.flow.readFirst")}</p>}
              {listen === "idle" && (
                <button type="button" className="ac-listen__button" onClick={() => void play()}>
                  <span className="ac-listen__disc" aria-hidden="true">
                    <Play size={30} strokeWidth={2.2} />
                  </span>
                  <span className="ac-listen__label">{t("academy.player.listen")}</span>
                </button>
              )}
              {(listen === "loading" || listen === "playing") && (
                <div className="ac-listen__playing" role="status">
                  <span className="ac-wave" aria-hidden="true">
                    {WAVE.map((n) => (
                      <span key={n} style={{ animationDelay: `${n * -0.13}s` }} />
                    ))}
                  </span>
                  <span>{t("academy.player.playing")}</span>
                </div>
              )}
              {listen === "done" && (
                <p className="ac-status ac-status--success">
                  <Headphones aria-hidden size={16} />
                  {t("academy.player.heard")}
                </p>
              )}
              {audioProblem && <p className="ko ac-footnote">{t("academy.player.audioProblem")}</p>}
            </div>
          </ExamAudioPanel>
        )}

        {skill === "ce" && question.document && (
          <div className="ac-document" lang="fr">
            {question.document}
          </div>
        )}

        {/* CO « image »: look at the picture, then hear the four propositions (official TCF). */}
        <ExamPictureView picture={question.image} lang="fr" />

        <h2 id={`q-${question.id}`} ref={stem} tabIndex={-1} className="ac-stem" lang="fr">
          {question.stem}
        </h2>

        <fieldset className="plain-fieldset ac-options-set" disabled={!optionsOpen || sending || correction !== null}>
          <legend className="visually-hidden">{t("academy.player.options")}</legend>
          {hasPictures(question.option_images) ? (
            <PictureOptions
              name={`q-${question.id}`}
              options={question.options}
              images={question.option_images}
              value={choice}
              onChange={setChoice}
              answer={correction?.answer ?? null}
              given={correction ? choice : null}
              lang="fr"
              legend={t("academy.player.options")}
            />
          ) : (
          <div className={`ac-options${question.options.length === 4 ? " ac-options--quad" : ""}`} lang="fr">
            {question.options.map((option, index) => {
              const state =
                correction === null
                  ? ""
                  : index === correction.answer
                    ? " ac-option--right"
                    : index === choice
                      ? " ac-option--wrong"
                      : "";
              return (
                <label key={index} className={`ac-option${choice === index ? " ac-option--on" : ""}${state}`}>
                  <input
                    type="radio"
                    name={`q-${question.id}`}
                    checked={choice === index}
                    onChange={() => setChoice(index)}
                  />
                  <span className="ac-option__letter" aria-hidden>
                    {letters[index]}
                  </span>
                  <span className="ac-option__text">{option}</span>
                  {choice === index && correction === null && <Check aria-hidden size={18} className="ac-option__check" />}
                </label>
              );
            })}
          </div>
          )}
        </fieldset>

        {correction && (
          <div className={`ac-correction${correction.correct ? " ac-correction--ok" : ""}`} role="status">
            <p className="ac-status">
              {correction.correct ? <CheckCircle2 aria-hidden size={18} /> : <XCircle aria-hidden size={18} />}
              <strong>{correction.correct ? t("academy.player.right") : t("academy.player.wrong", { letter: letters[correction.answer] ?? "" })}</strong>
            </p>
            {correction.explanation && <p>{correction.explanation}</p>}
          </div>
        )}

        {error && (
          <p className="ko" role="alert">
            {error}
          </p>
        )}
      </div>

      <ExamBar
        hint={
          !correction && (
            <>
              {t("academy.player.noReturn")}
              <span className="ac-xbar__keys"> · {t("examFrame.keysValidate")}</span>
            </>
          )
        }
        left={
          answerLeft !== null ? (
            <ExamCountdown seconds={answerLeft} label={t("examFrame.answerTime")} />
          ) : listen === "reading" && readingLeft !== null ? (
            <ExamCountdown seconds={readingLeft} label={t("examFrame.readingTime")} tone="reading" />
          ) : null
        }
        pills={
          <>
            <ExamPill>{t("academy.player.progress", { n: position, total: total || "…" })}</ExamPill>
            {showLevel && <ExamPill>{question.level}</ExamPill>}
          </>
        }
        right={
          correction ? (
            <Button size="lg" onClick={() => void onChanged()}>
              {t("academy.player.next")}
            </Button>
          ) : (
            <>
              {!training && <ExamAbandon />}
              <Button size="lg" onClick={() => void validate(choice)} disabled={choice === null || sending || !optionsOpen}>
                {sending ? t("common.loading") : t("academy.player.validate")}
              </Button>
            </>
          )
        }
      />
    </section>
  );
}

export function McqSection({
  attemptId,
  state,
  training,
  onChanged,
}: {
  attemptId: string;
  state: PlayerState;
  training: boolean;
  onChanged: () => Promise<void>;
}) {
  const [closing, setClosing] = useState(false);
  const skill = state.section === "co" ? "co" : "ce";

  if (!state.question) {
    // Every question answered (or the bank has no more): close the section on the server.
    const next = async () => {
      setClosing(true);
      try {
        await academyAttemptsNextCreate(attemptId);
      } finally {
        await onChanged();
        setClosing(false);
      }
    };
    return (
      <SectionDone onContinue={() => void next()} busy={closing} />
    );
  }

  return (
    <Question
      key={state.question.id}
      attemptId={attemptId}
      question={state.question}
      skill={skill}
      answered={state.answered}
      total={state.total}
      kind={state.kind}
      training={training}
      onChanged={onChanged}
    />
  );
}
